import { Component, OnInit, OnDestroy } from '@angular/core';
import { ISession } from '../../../models/session';
import { FIELD_TYPE_ICONS, IRegistrationField, IRegistrationFormOptions, RegistrationFormService } from '../registration-form.service';
import { CustomizationService } from '../../customization/customization.service';
import { RegistrationService } from '../registration.service';
import { ActivatedRoute } from '@angular/router';
import { DragulaService } from 'ng2-dragula';
import { Subscription } from 'rxjs/Subscription';

const DRAG_BAG = 'reg-fields-bag';

@Component({
  selector: 'sessions-registration-form',
  templateUrl: './registration-form.component.html',
  styleUrls: ['./registration-form.component.scss'],
})
export class SessionsRegistrationFormComponent implements OnInit, OnDestroy {
  public fieldTypeIcons = FIELD_TYPE_ICONS;
  public session: ISession;
  public fields: IRegistrationField[] = [];
  public visibleFieldsModel: IRegistrationField[] = [];
  public isLoading = false;
  public showEditModal = false;
  public isSaving = false;
  public editingField: IRegistrationField | null = null;
  public showDeleteModal = false;
  public deletingField: IRegistrationField | null = null;
  public dragulaOptions = {
    accepts: (el: any, _target: any, _source: any, sibling: any) => {
      const draggingDefault = el.dataset['isDefault'] === 'true';
      if (sibling === null) return !draggingDefault;
      const siblingIsDefault = sibling.dataset['isDefault'] === 'true';
      return draggingDefault ? siblingIsDefault : !siblingIsDefault;
    },
  };

  public options: IRegistrationFormOptions = {
    inUseOnly: false,
    displayKind: false,
    allowQuotas: false,
    allowCompanions: false,
    allowConditions: false,
    allowWaitingList: false,
  };

  public registrationEnabled = false;

  private _showInUseOnly = true;
  get showInUseOnly(): boolean { return this._showInUseOnly; }
  set showInUseOnly(value: boolean) {
    this._showInUseOnly = value;
    this.rebuildVisibleModel();
  }

  private dragulaSubscription: Subscription;
  private dragulaClonedSubscription: Subscription;

  constructor(
    private registrationFormService: RegistrationFormService,
    private registrationService: RegistrationService,
    private customizationService: CustomizationService,
    private route: ActivatedRoute,
    private dragulaService: DragulaService,
  ) {}

  ngOnInit() {
    this.customizationService.getSessionConfig().subscribe(config => {
      this.options = config.registrationFormOptions;
      console.log('registration form options:', this.options);
    });

    this.route.parent.data
      .map(route => route.data)
      .subscribe((session: ISession) => {
        this.session = session;
        this.load(session);
        this.registrationService.getRegistration(session).subscribe(res => {
          this.registrationEnabled = res.registration_enabled;
        });
      });

    this.dragulaSubscription = this.dragulaService.drop.subscribe(([bagName]: [string]) => {
      if (bagName !== DRAG_BAG) return;
      const visibleIds = new Set(this.visibleFieldsModel.map(f => f.option_id));
      const visibleQueue = [...this.visibleFieldsModel];
      const merged = this.fields.map(f => visibleIds.has(f.option_id) ? visibleQueue.shift()! : f);
      const defaults = merged.filter(f => f!.is_default);
      const customs = merged.filter(f => !f!.is_default);
      const order = [...defaults, ...customs].map(f => ({ option_id: f!.option_id, is_default: f!.is_default }));
      this.registrationFormService.reorderFields(this.session, order).subscribe(() => {
        this.load(this.session);
      });
    });

    this.dragulaClonedSubscription = this.dragulaService.cloned.subscribe((args: any[]) => {
      const [bagName, clone, original, type] = args;
      if (bagName !== DRAG_BAG || type !== 'mirror') return;
      clone.classList.remove('separator-before');
      const originalCells = original.children;
      const cloneCells = clone.children;
      for (let i = 0; i < originalCells.length; i++) {
        cloneCells[i].style.width = `${originalCells[i].offsetWidth}px`;
      }
    });
  }

  ngOnDestroy() {
    if (this.dragulaSubscription) {
      this.dragulaSubscription.unsubscribe();
    }
    if (this.dragulaClonedSubscription) {
      this.dragulaClonedSubscription.unsubscribe();
    }
  }

  load(session: ISession) {
    this.isLoading = true;
    this.registrationFormService.getRegistrationForm(session)
      .subscribe(
        form => {
          const sortByOrder = (arr: IRegistrationField[]) => arr.slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
          this.fields = [
            ...sortByOrder(form.fields.filter(f => f.is_default)),
            ...sortByOrder(form.fields.filter(f => !f.is_default)),
          ];
          this.rebuildVisibleModel();
          this.isLoading = false;
        },
        _err => { this.isLoading = false; }
      );
  }

  private rebuildVisibleModel() {
    this.visibleFieldsModel = this.fields.filter(f => {
      if (!this.options.allowCompanions && f.field_type === 'accompagnants') return false;
      if (this._showInUseOnly && f.is_default && !f.is_in_use) return false;
      return true;
    });
  }

  openEdit(field: IRegistrationField) {
    this.editingField = field;
    this.showEditModal = true;
  }

  openCreate() {
    this.editingField = null;
    this.showEditModal = true;
  }

  closeEditModal() {
    this.showEditModal = false;
    this.editingField = null;
  }

  onFieldSaved() {
    this.closeEditModal();
    this.load(this.session);
  }

  confirmDelete(field: IRegistrationField) {
    this.deletingField = field;
    this.showDeleteModal = true;
  }

  cancelDelete() {
    this.showDeleteModal = false;
    this.deletingField = null;
  }

  isFirstCustomField(field: IRegistrationField): boolean {
    if (field.is_default) return false;
    const idx = this.visibleFieldsModel.indexOf(field);
    return idx === 0 || this.visibleFieldsModel[idx - 1].is_default;
  }

  onRegistrationEnabledChange() {
    this.registrationService.updateRegistration(this.session, this.registrationEnabled).subscribe();
  }

  deleteField() {
    if (!this.deletingField || this.deletingField.is_default) return;
    this.registrationFormService.deleteCustomField(this.session, this.deletingField.option_id).subscribe(() => {
      this.fields = this.fields.filter(f => f.option_id !== this.deletingField!.option_id);
      this.rebuildVisibleModel();
      this.cancelDelete();
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/sessions/registration-form/registration-form.component.ts